import { useCallback, useMemo } from 'react';
import {
  createTreeItemsMap,
  getItemById,
  createOptimizedUtils,
  convertIdsToItems,
  getItemCheckedState,
} from './helpers';
import { processItemCheck } from './processors';
import { TreeCheckboxBaseNode, TreeItemsMap } from './types';

export type { TreeCheckboxBaseNode, TreeItemsMap };

export interface UseTreeCheckboxOptions<T extends TreeCheckboxBaseNode> {
  items: T[];
  checkedIds: string[];
  checkable?: boolean;
  onChecked?: (items: T[], checkedIds: string[]) => void;
}

export interface UseTreeCheckboxReturn<T extends TreeCheckboxBaseNode> {
  handleCheck: (item: T, checked: boolean) => void;
  getCheckState: (item: T) => { checked: boolean; indeterminate: boolean };
  treeMap: TreeItemsMap<T>;
}

/**
 * 트리 구조에서 체크박스 기능을 제공하는 훅
 *
 * @returns 체크박스 관련 함수와 상태, treeMap
 * @example
 * const { handleCheck, getCheckState, treeMap } = useTreeCheckbox({
 *   items: treeData,
 *   checkedIds: ['item1', 'item2'],
 *   onChecked: (items, ids) => {
 *     console.log('Checked items:', items);
 *     console.log('Checked IDs:', ids);
 *   }
 * });
 */
export function useTreeCheckbox<T extends TreeCheckboxBaseNode>({
  items,
  checkedIds,
  checkable = false,
  onChecked,
}: UseTreeCheckboxOptions<T>): UseTreeCheckboxReturn<T> {
  const treeMap = useMemo(() => createTreeItemsMap(items), [items]);
  const cacheUtils = useMemo(() => (checkable ? createOptimizedUtils<T>(treeMap) : null), [checkable, treeMap]);

  // 체크박스 상태 계산을 위한 체크 ID 집합
  const expandedCheckedIds = useMemo(() => {
    if (!checkable) return new Set<string>();

    const expanded = new Set<string>();
    checkedIds.forEach((id) => {
      const checkedItem = getItemById(id, treeMap);
      if (checkedItem && !checkedItem.disabled) {
        expanded.add(id);
        cacheUtils!.getEnabledDescendants(checkedItem).forEach((descendantId: string) => {
          expanded.add(descendantId);
        });
      }
    });
    return expanded;
  }, [checkable, cacheUtils, checkedIds, treeMap]);

  // 모든 아이템의 체크 상태 캐싱(checkedIds나 items가 바뀔 때만 재계산)
  const itemCheckStatesMap = useMemo(() => {
    if (!checkable) return new Map<string, { checked: boolean; indeterminate: boolean }>();

    const statesMap = new Map<string, { checked: boolean; indeterminate: boolean }>();
    treeMap.itemMap.forEach((item) => {
      const state = getItemCheckedState(item, expandedCheckedIds);
      statesMap.set(item.id, state);
    });
    return statesMap;
  }, [checkable, expandedCheckedIds, treeMap.itemMap]);

  const handleCheck = useCallback(
    (item: T, checked: boolean) => {
      if (!checkable || !onChecked || item.disabled) return;

      const normalizedCheckedIds = processItemCheck(item, checked, checkedIds, treeMap);
      const normalizedCheckedEntries = convertIdsToItems(normalizedCheckedIds, treeMap);

      onChecked(normalizedCheckedEntries, normalizedCheckedIds);
    },
    [checkable, checkedIds, onChecked, treeMap],
  );

  const getCheckState = useCallback(
    (item: T) => {
      if (!checkable) {
        return { checked: false, indeterminate: false };
      }

      const cachedState = itemCheckStatesMap.get(item.id);
      if (cachedState) {
        return cachedState;
      }
      return { checked: false, indeterminate: false };
    },
    [checkable, itemCheckStatesMap],
  );

  return {
    handleCheck,
    getCheckState,
    treeMap,
  };
}
